import React, { useState } from 'react';
import type { ProposalView } from '../utils/nltWorkflowProposals';
import { summarizeProposals } from '../utils/nltWorkflowProposals';
import NltTextFieldDiffLines from './NltTextFieldDiffLines';
import NltProposalDiffModal from './NltProposalDiffModal';
import './NLTWorkflowProposals.css';

export interface NLTWorkflowProposalsProps {
  views: ProposalView[];
}

/**
 * Every operation the agent proposed, applied or not. Dropped ones carry their reason
 * — that is what tells a person how to rephrase the request, so nothing here filters
 * the list down to what landed.
 */
const NLTWorkflowProposals: React.FC<NLTWorkflowProposalsProps> = ({ views }) => {
  const [expanded, setExpanded] = useState(true);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  // Панель узкая: сводка остаётся в ней, а разбор правки открывается во всю ширину.
  const [zoomed, setZoomed] = useState<ProposalView | null>(null);

  if (views.length === 0) return null;
  const stats = summarizeProposals(views);

  return (
    <div className={`nwp${expanded ? ' nwp--expanded' : ''}`}>
      <button type="button" className="nwp__head" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}>
        <i className={`fas fa-chevron-${expanded ? 'down' : 'right'} nwp__chevron`} aria-hidden="true" />
        <span className="nwp__title">Proposals</span>
        <span className="nwp__count">{stats.total}</span>
        {stats.applied > 0 ? <span className="nwp__count nwp__count--ok">{stats.applied} applied</span> : null}
        {stats.notApplied > 0 ? <span className="nwp__count nwp__count--muted">{stats.notApplied} not</span> : null}
      </button>

      {expanded ? (
        <ul className="nwp__list">
          {views.map((view, i) => {
            const open = openIndex === i;
            const { proposal } = view;
            return (
              <li key={`${proposal.index}-${proposal.address}`} className={`nwp__item nwp__item--${view.outcome}`}>
                <button
                  type="button"
                  className="nwp__item-head"
                  onClick={() => setOpenIndex(open ? null : i)}
                  aria-expanded={open}
                >
                  {/* Две строки, а не одна: панель NLT узкая, и в один ряд адрес
                      схлопывался до «pro…» — то есть до нечитаемого. */}
                  <span className="nwp__head-top">
                    <span className={`nwp__badge nwp__badge--${view.outcome}`}>{view.outcomeLabel}</span>
                    {view.where.scope !== 'unknown' ? (
                      <span className={`nwp__scope nwp__scope--${view.where.scope}`}>{view.where.scope}</span>
                    ) : null}
                    {proposal.operation?.op ? <span className="nwp__op">{proposal.operation.op}</span> : null}
                    <i className={`fas ${open ? 'fa-chevron-up' : 'fa-chevron-down'} nwp__chevron`} aria-hidden="true" />
                  </span>
                  <span className="nwp__where" title={proposal.address}>
                    {view.where.owner ? <span className="nwp__owner">{view.where.owner}</span> : null}
                    {view.where.path.slice(0, -1).map((part, pi) => (
                      <span key={`${part}-${pi}`} className="nwp__crumb">
                        {part}
                      </span>
                    ))}
                    <span className="nwp__field">{view.where.field}</span>
                  </span>
                </button>

                {!view.writable ? (
                  <p className="nwp__note nwp__note--warn">
                    <i className="fas fa-lock" aria-hidden="true" /> Global section — this connector only accepts writes
                    to <code>workflow</code>, so this one cannot be applied from here.
                  </p>
                ) : null}

                {view.reason ? <p className="nwp__note">{view.reason}</p> : null}

                {open ? (
                  <div className="nwp__body">
                    <button
                      type="button"
                      className="nwp__zoom"
                      onClick={() => setZoomed(view)}
                      title="Open this change full width"
                    >
                      <i className="fas fa-up-right-and-down-left-from-center" aria-hidden="true" /> Open full view
                    </button>
                    {!view.resolved ? (
                      <p className="nwp__note nwp__note--warn">
                        Address <code>{proposal.address}</code> was not found in the workflow that was sent, so there is
                        nothing to compare — most likely the graph moved between reading and this turn.
                      </p>
                    ) : view.hasValueDiff ? (
                      // Объекты и списки печатаются многострочным JSON, поэтому
                      // построчный diff читается по ним так же, как по тексту.
                      <div className="nwp__diff">
                        <NltTextFieldDiffLines
                          oldValue={view.oldValue}
                          newValue={view.newValue}
                          mode="pending"
                          variant="summary"
                        />
                      </div>
                    ) : view.outcome === 'applied' ? (
                      <>
                        <div className="nwp__value-label">value at this address</div>
                        <pre className="nwp__value">{view.oldValue || '—'}</pre>
                        <p className="nwp__note">
                          The value at this address is unchanged — the operation landed without altering it.
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="nwp__value-label">current value at this address</div>
                        <pre className="nwp__value">{view.oldValue || '—'}</pre>
                      </>
                    )}
                    {proposal.operation?.value !== undefined ? (
                      <>
                        <div className="nwp__value-label">proposed operation</div>
                        <pre className="nwp__value">
                          {typeof proposal.operation.value === 'string'
                            ? proposal.operation.value
                            : JSON.stringify(proposal.operation.value, null, 2)}
                        </pre>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}

      <NltProposalDiffModal view={zoomed} onClose={() => setZoomed(null)} />
    </div>
  );
};

export default NLTWorkflowProposals;
